import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { ValTableState } from '@valamar/val-table';
import { ValToastService } from '@valamar/val-toast';
import { AccountInterface } from '../interfaces/vrc/account.interface';
import { ValUtilsService } from './val-utils.service';
import { VrcAccountUtilsService } from './vrc-account-utils.service';
import { VrcDataService } from './vrc-data.service';
import { VrcStorageService } from './vrc-storage.service';

export const vrcAccountResolver: ResolveFn<boolean> =
  (route: ActivatedRouteSnapshot) => {
    const _srvc = inject(VrcDataService);
    const _st = inject(VrcStorageService);
    const _ms = inject(ValToastService);
    const _u = inject(ValUtilsService);
    const _r = inject(Router);
    const _au = inject(VrcAccountUtilsService);
    const id = route.params['id'];
    const _setReservationSummary = () => {
      const paramsModel = {} as ValTableState;
      paramsModel.first = 0;
      paramsModel.rows = 5;
      paramsModel.multiSortMeta = [{ field: 'checkIn', order: -1 }];
      (paramsModel as any).extraData = _st.editModel().id;
      _srvc.getReservationPage(paramsModel)
        .pipe(
          take(1),
          tap(result => {
            if (result && result.data) {
              _st.editModel.update(x => ({ ...x, reservations: result.data }));
            }
          }),
          catchError((err: HttpErrorResponse) => {
            _ms.danger({ title: 'Error', message: err?.error?.message, position: 'bottom-right', autoCloseMs: 3000 });
            return of(null);
          })
        )
        .subscribe();
    };
    const _setOfferSummary = () => {
      const paramsModel = {} as ValTableState;
      paramsModel.first = 0;
      paramsModel.rows = 5;
      paramsModel.multiSortMeta = [{ field: 'checkIn', order: -1 }];
      paramsModel.filters = {
        accountId: [
          {
            value: _st.editModel().id,
            matchMode: 'equals',
            operator: 'and',
          }],
      };
      _srvc.getOfferPage(paramsModel)
        .pipe(
          take(1),
          tap(result => {
            if (result && result.data) {
              _st.editModel.update(x => ({ ...x, offers: result.data }));
            }
          }),
          catchError((err: HttpErrorResponse) => {
            _ms.danger({ title: 'Error', message: err?.error?.message, position: 'bottom-right', autoCloseMs: 3000 });
            return of(null);
          })
        )
        .subscribe();
    };
    if (id == 'new') {
      _st.interestsLookup = _st.interestsLookup ? _st.interestsLookup : [];
      _st.isEditOn.set(true);
      const acc = _au.initNewAccount(_st.firstName, _st.lastName, _st.language, _st.phone, _st.email);
      _st.origModel = acc;
      const editModel = _au.mapAccountToEditAccount(acc);
      const phones = _au.mapAccountToPhones(acc);
      const emails = _au.mapAccountToEmails(acc);
      _st.editModel.set(editModel);
      _st.emails.set(emails);
      _st.phones.set(phones);
      _st.loyality = [];
      _st.emails.set([]);
      _st.phones.set([]);
      return of(true);
    }

    _st.niceNewContact = false;

    _ms.info({ title: 'Loading', message: 'Loading account', position: 'bottom-right', autoCloseMs: 3000 });
    return _srvc.getAccountById(id).pipe(
      take(1),
      map(acc => {
        // model fix
        if (acc) {
          acc = <AccountInterface>_au.AccountFix(acc);
          const editModel = _au.mapAccountToEditAccount(acc);
          _st.editModel.set(editModel);
          _st.origModel = acc;
          _st.loyality = acc.loyaltiesList;
          _st.loyalityOrig = _u.deepCopyObject(acc.loyaltiesList);
          if (_st.mergeAccounts() && _st.duplicateAccount) {
            _st.editModel.set(_u.deepMergeObjectValues(_st.duplicateAccount, _st.editModel(), false));
            _st.duplicateAccount = null;
          }
          const phones = _au.mapAccountToPhones(acc);
          const emails = _au.mapAccountToEmails(acc);
          _st.emails.set(emails);
          _st.phones.set(phones);
        }

        if (_st.accountIsSelected) {
          _st.accountIsSelected = false;
          _au.UpdateCommunicationAccountData();
        } else {
          _au.synchronizeActivatedContact();
        }
        _setOfferSummary();
        _setReservationSummary();

        _st.selectedLoyaltyNumber.set(_st.editModel().loyaltiesList?.filter(x => !x.cardInformation?.cardNumber?.startsWith("4")).map(x => x.cardInformation?.cardNumber).reduceRight((_a, b) => b, null) ?? null);
        _st.selectedValamartistNumber.set(_st.editModel().loyaltiesList?.filter(x => x.cardInformation?.cardNumber?.startsWith("4")).map(x => x.cardInformation?.cardNumber).reduceRight((_a, b) => b, null) ?? null);
        _st.emails.set(_st.editModel().contact?.additional?.emails.map((e, i) => {
          return { id: i, data: e };
        }) ?? []);
        _st.phones.set(_st.editModel().contact?.additional?.phones.map((e, i) => {
          return { id: i, data: e };
        }) ?? []);

        return true;
      }),
      catchError((err: HttpErrorResponse) => {
        _ms.danger({ title: 'Error', message: err?.error?.message, position: 'bottom-right', autoCloseMs: 3000 });
        _r.navigate(['../']);
        return of(false);
      })
    );
  };
